// Parties list (spec §6.6 entry point).
//
// Customers and merchants only. The Own accounts tab — which opened on the
// folder rows and asked the operator to drill in — went on 23 Sept 2026:
// Treasury already lists every desk account, with balances and grouped by what
// the account is, so this screen no longer keeps a second, thinner copy of it.
// Nor does a typed search reach one (21 Oct 2026: the operator still saw them —
// lib/ownAccounts.ts): a desk account opens on Treasury, from here, the palette
// and the Wallet register alike.
//
// Desktop console (lg+): favorites pinned as cards, list rendered as a dense
// table. Below lg the original stacked layout stays, plus the star toggle.
//
// NAMING + RELEVANCE (2026-08-12 operator pass):
//   · Names never render from the stored string — `partyName()` normalizes,
//     `partySubtitle()` produces the one muted `Kind · Standing · State` line
//     that distinguishes a customer from a merchant from a husk. That line is
//     present on BOTH layouts (2 slots below lg, 3 at lg+), because the
//     operator asked for distinguishability "at all the screens".
//   · Ordering is the shared comparator (tier → |balance| → recency → name),
//     so favorites float without a bespoke sort and dormant parties sink.
//   · Tier 3 (stale) is parked behind a true-count `Show all (N)`; nothing is
//     ever dropped, and a search reaches every tier.
//   · Raw enums are gone: party_type / trust_tier are never printed — the
//     subtitle carries them in sentence case.
//
// Minimised 14 Sept 2026 (sweep §5.16 / item 56): the "Tap a group" hint is
// gone (the folder glyph says it), the empty line is five words, the footer
// reads "Net · N parties" with the one sign-law sentence as its legend, the
// balance colours are the one negative token, and every counterparty reads
// "Merchant".
//
// 21 Oct 2026 audit (operator: "I need clean view for operator"): no subtitle (it
// restated the chips); "Pinned 5" where it said "Favorites  5 pinned"; the
// sign-law line moved into About this screen; PINNED PARTIES OBEY Show balances
// on the phone and the console alike (the phone's pinned rows showed none with it
// on, the console's cards showed them with it off); an empty balance bag is ONE
// dash across the table's columns, not one per asset, and says nothing on the phone
// or a card — NEVER "Square": the list's balances are the BFF's best effort (a
// ledger read that fails comes back as `{}`, bff/src/routes/parties.ts), so an
// empty bag is not proof of square (Astra, 21 Oct); the phone says "KYC pending" /
// "KYC rejected" instead of a bare "KYC" told apart by colour; "+ New" is a button
// and the search box a box on the phone too.
//
// 22 Oct 2026 design-system sweep: a failed read is ONE red line (what failed, Try again, the server's sentence a tap away) and no
// stray empty card under it; the empty lines are the kit's EmptyState; a party's NAME WRAPS instead of truncating (a 37-character
// merchant read "Northern Gulf Commoditi…" beside a "Mer…" that said nothing — the kind word now follows the name or drops
// under it); the balance figures of the table are body size (they are the values, not a hint); the search is 16 px on a phone
// (iOS zoomed the page on focus); the star, "+ New" and the balances switch are 44 px targets on a phone.
// With the switch on and the BFF saying `balances_read: false` (a ledger list could not be read) ONE amber line says "Balances not read."
// with a Try again — over whatever figures did come back (a partial read keeps them); the neutral "No balances listed." is for a list
// the BFF called read (or did not flag) that simply carries no figure.

import { useEffect, useState } from 'react';
import { errorWords, readWords } from '../lib/errors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { FolderOpen, ChevronRight, Star, Check } from 'lucide-react';
import { api, type PartySummary } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { SignLaw } from '../components/SignLaw';
import { Pending, Pill, StateLine, EmptyState, Label } from '../components/kit';
import { collapseTotals, fmtAmount } from '../lib/format';
import {
  partyName, partySubtitle, needsNameReview, compareRelevance, partitionByRelevance,
} from '../lib/naming';
import { tg } from '../lib/telegram';
import { partiesOnly } from '../lib/ownAccounts';
import { cutRosterWords, rosterCut } from '../lib/roster-cut';

// 'all' is not a party_type — it is the absence of the filter, so the list
// endpoint is called without `type` and every family comes back in one table.
// The operator asked for it because the three tabs made it impossible to see
// the desk in one view.
const TYPE_FILTERS = ['all', 'customer', 'counterparty'] as const;
type TypeFilter = typeof TYPE_FILTERS[number];
const TYPE_LABEL: Record<TypeFilter, string> = {
  all:           'All',
  customer:      'Customers',
  counterparty:  'Merchants',
};

/** Column order for the balance table. Anything not listed sorts after these,
 *  alphabetically, so a new asset appears rather than being silently dropped. */
const ASSET_ORDER = ['AED', 'INR', 'USD', 'EUR', 'USDT'];
function orderAssets(assets: Iterable<string>): string[] {
  return [...new Set(assets)].sort((a, b) => {
    const ia = ASSET_ORDER.indexOf(a), ib = ASSET_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });
}

/** A balance cell: signed, monospaced, the one negative token for a box the
 *  desk holds (SignLaw is the legend). Zero and absent are DIFFERENT — a party
 *  holding exactly 0 of an asset it has traded is not the same as one that
 *  never held it, so 0 renders and absent is a dash. */
function BalanceCell({ v, asset }: { v: number | undefined; asset: string }) {
  if (v === undefined) return <span className="text-tg-hint/40">—</span>;
  const cls = v < 0 ? 'text-neg' : v === 0 ? 'text-tg-hint' : '';
  return <span className={`amt ${cls}`}>{fmtAmount(v, asset)}</span>;
}

export function Parties() {
  const [type, setType] = useState<TypeFilter>('customer');
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  // Balances are opt-in: the list endpoint reads all three account families
  // from the ledger to build them (~2.7s against production), which is worth
  // paying for a balance table and not worth paying to pick a party.
  const [withBalances, setWithBalances] = useState(false);
  const qc = useQueryClient();

  const trimmedQ = q.trim();

  // "Show all" is a per-view escape hatch, not a preference.
  useEffect(() => { setShowAll(false); }, [type, trimmedQ]);

  const listKey = ['parties', type, trimmedQ, withBalances] as const;
  const query = useQuery({
    queryKey: listKey,
    queryFn: () => api.listParties({
      entity:   'dubaiotc',
      // 'all' means: send no type filter at all.
      type:     type === 'all' ? undefined : type,
      q:        trimmedQ || undefined,
      with_balances: withBalances || undefined,
      // Ask for the whole roster. 500 is the endpoint's ceiling and the desk has
      // 134 active parties; the 100 default was hiding 34 of them on the All tab.
      pageSize: 500,
    }),
  });

  // THE DESK'S OWN ACCOUNTS LIVE ON TREASURY (operator, 23 Sept 2026: "move
  // this to treasury only, we already have it separately and groups as well").
  // The tab that listed them is gone: it opened on folders, and 14 of the 16
  // folders were archived migration leftovers while 64 live accounts sat in no
  // folder at all — it reached 7 of 71. Treasury lists every one of them with
  // balances, grouped by what the account IS. A typed search used to still find
  // an account by name; 21 Oct 2026 the operator, still seeing them: "in party,
  // I am still seeing treasury accounts, which it should't display and anyway
  // it's wrong" — and the page it opened had no balances, no wallets and an
  // empty statement. Never listed, searched or not.
  const rows: PartySummary[] = partiesOnly(query.data?.parties ?? []);

  // One comparator, one partition — the screen states a maxTier and never
  // writes a predicate of its own. Searching reaches every tier.
  const ordered = [...rows].sort(compareRelevance);
  const { shown, hidden } = partitionByRelevance(ordered, { maxTier: trimmedQ ? 3 : 2 });

  // Tier 0 is the pinned set; it renders once, in its own section, and is not
  // repeated in the list below (it used to appear twice below lg).
  const pinned = shown.filter((p) => p.is_favorite);
  const rest   = shown.filter((p) => !p.is_favorite);

  // PINNED PARTIES OBEY THE "SHOW BALANCES" SWITCH, on both layouts (21 Oct 2026
  // audit). The desktop cards used to read each favourite's party page for their
  // balances — whatever the switch said, and one slow ledger read per card on
  // every visit — while the phone's pinned rows were the one place with the
  // switch on and no balance. Now the cards and the rows read what the list
  // itself carries (with_balances), which is also what the table's "Net" sums, so
  // the card, the row and the foot always agree. Switch off: no balance anywhere.

  const toggleFavorite = useMutation({
    mutationFn: ({ slug, favorite }: { slug: string; favorite: boolean }) =>
      api.favoriteParty(slug, favorite),
    onMutate: async ({ slug, favorite }) => {
      await qc.cancelQueries({ queryKey: listKey });
      const prev = qc.getQueryData<{ parties: PartySummary[] }>(listKey);
      if (prev) {
        qc.setQueryData(listKey, {
          ...prev,
          parties: prev.parties.map((p) => p.slug === slug ? { ...p, is_favorite: favorite } : p),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      // The row used to snap back with only a phone buzz — invisible on desktop,
      // where there is no haptic at all (3 Sept audit).
      if (ctx?.prev) qc.setQueryData(listKey, ctx.prev);
      tg()?.HapticFeedback.notificationOccurred('error');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['parties'] }),
  });
  // …and says so, once, above the list.
  const favError = toggleFavorite.isError ? errorWords(toggleFavorite.error) : null;

  const onToggle = (slug: string, favorite: boolean) => toggleFavorite.mutate({ slug, favorite });

  // THE BFF'S OWN WORD ON THE BALANCES (Mechanisms, 5c4fa78): with `with_balances=true` the list answers `balances_read: false` when any of
  // the three ledger lists could not be read, `true` when all were, and carries NO flag without the switch. The rows keep whatever
  // WAS read, so a false flag with figures on screen is a PARTIAL read.
  const balancesRead = query.data?.balances_read;
  const balancesUnread = withBalances && balancesRead === false;
  // THE ROSTER WENT PAST ITS PAGE (`more: true`): what is listed is the FIRST of them — it is not "all the parties", and a name that is
  // not here may still exist (the search asks the books). The count is the PAGE read — the desk's own accounts are among its rows and are
  // not drawn here — and a page that held only those lists nobody without being an empty roster: the empty states wait for a whole one.
  const partialRoster = rosterCut(query.data);

  // Two different notions of empty: nothing came back at all (real empty
  // state) vs. everything left is dormant and collapsed (the Show all footer
  // speaks for that, so no scary "no parties" copy).
  const nothingAtAll = rows.length === 0;
  const visibleCount = rest.length + (showAll ? hidden.length : 0);

  // Columns come from the rows on screen, not a fixed list — an asset the desk
  // starts holding appears by itself. Balances arrive already collapsed per
  // party (stables netted to one USDT key server-side); collapseTotals() then
  // renders a desk account's chain-bearing code as USDT·TRC and friends.
  const balanceRows = new Map<string, Record<string, number>>();
  if (withBalances) {
    for (const p of [...pinned, ...rest, ...(showAll ? hidden : [])]) {
      if (p.balances) balanceRows.set(p.slug, collapseTotals(p.balances));
    }
  }
  const assetCols = withBalances
    ? orderAssets([...balanceRows.values()].flatMap((b) => Object.keys(b)))
    : [];

  // THE KYC COLUMN EARNS ITS HEADER ONLY WHEN A ROW HAS A MARK (7 Sept 2026).
  // Own accounts never carry one, and a list of desk accounts — or of
  // customers the pipeline has not touched — was printing an empty column
  // under a heading.
  const showKyc = [...rest, ...(showAll ? hidden : [])].some(hasKycMark);

  // Cumulative per asset across every party on screen (operator, 2026-08-28).
  // Signed, so it reads as the desk's NET position against the whole book: a
  // positive column is owed TO the desk, a negative one is owed BY it. It sums
  // exactly the rows rendered — turn on Dormant and the total moves with it,
  // because a total that silently covers rows you cannot see is worse than none.
  const totals: Record<string, number> = {};
  for (const b of balanceRows.values()) {
    for (const [a, v] of Object.entries(b)) totals[a] = (totals[a] ?? 0) + v;
  }

  return (
    <div className="space-y-3">
      {/* 21 Oct 2026 audit: no subtitle — "Customers and merchants" restated the
          three chips under it — and "+ New" is a button on the phone too (it was
          a bare blue word; the gold fill was lg+ only). It is the screen's one
          primary action, so it is the one gold button at every width. */}
      <ScreenHeader
        title="Parties"
        actions={
          <Link
            to="/parties/new"
            className="btn-primary text-body py-1.5 px-3.5 max-lg:inline-flex max-lg:items-center max-lg:min-h-11 lg:rounded-md lg:py-2 lg:hover:opacity-90 lg:transition-opacity"
          >
            + New<span className="hidden lg:inline"> party</span>
          </Link>
        }
        help={
          <>
            <p>
              Customers trade and settle on the spot; merchants carry a running position and can
              settle later. The desk&rsquo;s own accounts are on Wallets.
            </p>
            <p>
              Show balances reads every party&rsquo;s box (a slower read) and adds a column per asset
              with the desk&rsquo;s net at the foot. Dormant parties are folded behind Show all, never
              dropped; a search reaches them.
            </p>
            {/* THE LAW, ONCE, for the page that carries a sign (§5.16: "the list
                has colours and no law"). It stood as a line under the switch;
                21 Oct 2026 audit: it lives here, in the page's About, in the
                one wording (SignLaw). */}
            <SignLaw />
          </>
        }
      />

      {/* Search + type chips. Mobile keeps the stacked layout (space-y matches
          the parent rhythm); lg+ folds them into one console toolbar row with
          mockup-style pill chips. */}
      <div className="space-y-3 lg:space-y-0 lg:flex lg:items-center lg:gap-2.5">
        <input
          className="w-full bg-panel border border-stroke p-2.5 rounded text-field lg:text-body focus:outline-none focus:border-tg-accent lg:flex-1 lg:bg-panel lg:border lg:border-stroke lg:rounded-md lg:px-3 lg:py-2 lg:placeholder:text-tg-hint lg:focus:outline-none lg:focus:border-tg-accent"
          placeholder="Search party"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 lg:gap-1.5 lg:overflow-visible lg:pb-0 lg:mx-0 lg:px-0 lg:shrink-0">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`chip ${type === t ? 'chip-on' : ''}`}
            >
              {TYPE_LABEL[t]}
            </button>
          ))}
        </div>

        {/* A SWITCH, NOT A FOURTH FILTER.
            This first shipped styled exactly like the three filter chips beside
            it, so it read as another mutually-exclusive filter and simply looked
            un-selected — the balances appeared "missing" when the control had
            never been pressed. Separated, given a tick, and labelled with what
            it does rather than what it is. */}
        <label
          className={`flex items-center gap-1.5 shrink-0 cursor-pointer select-none rounded-md border px-2.5 py-1 text-body max-lg:min-h-11 lg:text-meta whitespace-nowrap transition-colors ${
            withBalances
              ? 'bg-tg-accent/[0.14] text-tg-accent border-tg-accent'
              : 'bg-transparent text-tg-hint border-dashed border-stroke hover:text-tg-text hover:border-brand-muted'
          }`}
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={withBalances}
            onChange={() => setWithBalances((v) => !v)}
          />
          <Check size={13} className={withBalances ? 'opacity-100' : 'opacity-30'} />
          Show balances
        </label>
      </div>

      {query.isLoading && <Pending />}
      {/* A FAILED READ: one red line — what failed, Try again, the server's sentence a tap away — never the "No parties yet" below. */}
      {query.isError && (
        <StateLine
          tone="bad"
          more={readWords(query.error)}
          action={<button type="button" className="btn-link text-body" disabled={query.isFetching} onClick={() => query.refetch()}>{query.isFetching ? 'Trying…' : 'Try again'}</button>}
        >
          Couldn&rsquo;t read the parties.
        </StateLine>
      )}
      {favError && <StateLine tone="bad" more={favError}>Couldn&rsquo;t change the star.</StateLine>}
      {/* SWITCH ON AND NOT ONE BAG CARRYING A FIGURE — say so, neutrally (Astra, 21 Oct). The
          BFF turns a failed ledger read into `balances: {}` on every row, the same payload as a
          desk that is square, so "no figures" would otherwise read as an all-clear. This line
          claims nothing about the ledger — only what the list holds. */}
      {partialRoster && <StateLine>{cutRosterWords(query.data?.parties.length ?? rows.length, 'search')}</StateLine>}
      {balancesUnread && (
        <StateLine
          tone="warn"
          action={<button type="button" className="btn-link text-body" disabled={query.isFetching} onClick={() => query.refetch()}>{query.isFetching ? 'Trying…' : 'Try again'}</button>}
        >
          Balances not read.
        </StateLine>
      )}
      {!balancesUnread && withBalances && shown.length > 0 && shown.every((p) => !p.balances || Object.keys(p.balances).length === 0) && (
        <StateLine>No balances listed.</StateLine>
      )}

      {pinned.length > 0 && (
        <section>
          {/* "Pinned 5" — the label said "Favorites  5 pinned" (21 Oct 2026 audit). */}
          <div className="flex items-baseline gap-2 mb-1.5 px-1 lg:mb-2.5 lg:px-0.5">
            <h2><Label>Pinned</Label></h2>
            <span className="font-mono tabular text-meta text-tg-hint">{pinned.length}</span>
          </div>
          <div className="hidden lg:grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2">
            {pinned.map((p) => (
              <FavoriteCard key={p.id} party={p} balances={withBalances ? p.balances : undefined} onToggle={onToggle} />
            ))}
          </div>
          <div className="lg:hidden card-tight py-0 divide-y divide-tg-secondary">
            {pinned.map((p) => <PartyRow key={p.id} party={p} onToggle={onToggle} balances={balanceRows.get(p.slug)} kind={type === 'all'} />)}
          </div>
        </section>
      )}

      {pinned.length > 0 && rest.length > 0 && (
        <div className="flex items-baseline gap-2 px-1 -mb-1 lg:px-0.5 lg:-mb-0.5">
          <h2><Label>All parties</Label></h2>
          <span className="font-mono tabular text-meta text-tg-hint">{rest.length}</span>
        </div>
      )}

      {/* Below lg — ONE LIST, ONE LINE PER PARTY (operator, 19 Sept 2026:
          "fix the phone layout for the parties list screen too" — a card per
          party, two lines each, was a screen of padding). The kind word is
          dropped when the tab already says it. */}
      {(rest.length > 0 || (showAll && hidden.length > 0)) && (
        <div className="lg:hidden card-tight py-0 divide-y divide-tg-secondary">
          {rest.map((p) => <PartyRow key={p.id} party={p} onToggle={onToggle} balances={balanceRows.get(p.slug)} kind={type === 'all'} />)}
          {showAll && hidden.length > 0 && (
            <>
              <p className="pt-2.5 pb-1"><Label>Dormant</Label></p>
              {hidden.map((p) => <PartyRow key={p.id} party={p} onToggle={onToggle} dimmed balances={balanceRows.get(p.slug)} kind={type === 'all'} />)}
            </>
          )}
        </div>
      )}
      {query.data && nothingAtAll && !query.isError && !partialRoster && (
        <EmptyState className="lg:hidden">{trimmedQ ? 'Nothing here.' : 'No parties yet.'}</EmptyState>
      )}

      {/* lg+ — dense console table when balances stand; otherwise a GRID of
          the one-line rows (21 Sept 2026): a table with one text column
          across a 1440px console was a name at the left and a blank to the
          right on forty rows — two or three columns of rows read the same
          list in a third of the height. */}
      <div className="hidden lg:block">
        {visibleCount > 0 && assetCols.length === 0 ? (
          <div className="bg-panel rounded-xl border border-stroke px-4 pb-2 grid grid-cols-2 xl:grid-cols-3 gap-x-8 [&>a]:border-b [&>a]:border-stroke-soft">
            {/* Every row carries its rule and the panel pads under the last
                row, so the foot reads even whatever the count; a row lights
                on hover as the table's rows do. */}
            {rest.map((p) => <PartyRow key={p.id} party={p} onToggle={onToggle} kind={type === 'all'} className="py-2.5 lg:-mx-2 lg:px-2 lg:hover:bg-tg-secondary" />)}
            {showAll && hidden.length > 0 && (
              <>
                <p className="col-span-full pt-3 pb-1"><Label>Dormant</Label></p>
                {hidden.map((p) => <PartyRow key={p.id} party={p} onToggle={onToggle} dimmed kind={type === 'all'} className="py-2.5 lg:-mx-2 lg:px-2 lg:hover:bg-tg-secondary" />)}
              </>
            )}
          </div>
        ) : visibleCount > 0 ? (
          <div className="bg-panel rounded-xl border border-stroke overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-body">
              <thead>
                <tr className="text-left text-label font-medium uppercase tracking-[0.06em] text-tg-hint border-b border-stroke-soft">
                  <th className="w-10 px-3 py-2 font-medium" aria-label="Favorite" />
                  <th className="px-3 py-2 font-medium">Party</th>
                  {assetCols.map((a) => (
                    <th key={a} className="px-3 py-2 font-medium text-right whitespace-nowrap">{a}</th>
                  ))}
                  {showKyc && <th className="w-24 px-3 py-2 font-medium">KYC</th>}
                  <th className="w-10 px-3 py-2" aria-label="Open" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stroke-soft">
                {rest.map((p) => (
                  <PartyTableRow key={p.id} party={p} onToggle={onToggle} kind={type === 'all'}
                                 assetCols={assetCols} balances={balanceRows.get(p.slug)} showKyc={showKyc} />
                ))}
                {showAll && hidden.length > 0 && (
                  <>
                    <tr>
                      <td colSpan={(showKyc ? 4 : 3) + assetCols.length} className="px-3 py-1.5 text-label uppercase tracking-[0.09em] text-tg-hint bg-panel-2">
                        Dormant
                      </td>
                    </tr>
                    {hidden.map((p) => (
                      <PartyTableRow key={p.id} party={p} onToggle={onToggle} dimmed kind={type === 'all'}
                                     assetCols={assetCols} balances={balanceRows.get(p.slug)} showKyc={showKyc} />
                    ))}
                  </>
                )}
              </tbody>
              {assetCols.length > 0 && (
                <tfoot>
                  <tr className="border-t border-stroke bg-panel-2">
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2 text-label font-medium uppercase tracking-[0.08em] text-tg-hint">
                      Net · {balanceRows.size} {balanceRows.size === 1 ? 'party' : 'parties'}
                    </td>
                    {assetCols.map((a) => (
                      <td key={a} className="px-3 py-2 text-right whitespace-nowrap text-body font-medium">
                        <BalanceCell v={totals[a]} asset={a} />
                      </td>
                    ))}
                    {showKyc && <td className="px-3 py-2" />}
                    <td className="px-3 py-2" />
                  </tr>
                </tfoot>
              )}
            </table>
            </div>
          </div>
        ) : query.data && nothingAtAll && !query.isError && !partialRoster ? (
          <EmptyState>{trimmedQ ? 'Nothing here.' : 'No parties yet.'}</EmptyState>
        ) : null}
      </div>

      {/* Dormant parties are hidden, never deleted — and the count is real. */}
      {hidden.length > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="btn-link text-meta px-1 max-lg:py-3 lg:px-0.5"
        >
          {showAll ? 'Show fewer' : `Show all (${hidden.length})`}
        </button>
      )}
    </div>
  );
}

interface ToggleProps {
  onToggle: (slug: string, favorite: boolean) => void;
}

/** own_account with treasury_slug === null is a group (parent bucket). The
 *  folder glyph is an affordance, not a label — the kind itself is spelled out
 *  by partySubtitle(). */
function isGroupParty(party: PartySummary): boolean {
  return party.party_type === 'own_account' && party.treasury_slug === null;
}

function StarButton({ party, onToggle, className = '' }: { party: PartySummary; className?: string } & ToggleProps) {
  const fav = !!party.is_favorite;
  const label = fav ? 'Remove from favorites' : 'Add to favorites';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle(party.slug, !fav);
      }}
      className={`p-3 -m-2.5 rounded transition-colors hover:bg-tg-bg max-lg:flex max-lg:h-11 max-lg:w-11 max-lg:items-center max-lg:justify-center max-lg:-my-3 max-lg:-mr-3 max-lg:ml-0 ${className}`}
    >
      <Star size={14} className={fav ? 'text-tg-accent fill-current' : 'text-tg-hint'} />
    </button>
  );
}

/** Not for the desk's own accounts: a bank account has no KYC to do, and the
 *  default 'pending' on 64 desk-account rows put an amber dot on most of the
 *  list (6 Sept 2026). */
function hasKycMark(p: PartySummary): boolean {
  return p.party_type !== 'own_account'
    && (p.kyc_status === 'approved' || p.kyc_status === 'pending' || p.kyc_status === 'rejected');
}

/** The KYC state, where there is one to say: a tick for approved, a word
 *  in a pill for pending and rejected — the pending mark was a middle dot,
 *  invisible on a 1440px row (21 Sept 2026). */
function KycMark({ status }: { status: string }) {
  if (status === 'approved') return <span className="text-pos text-body" title="KYC approved">✓</span>;
  // The pill at lg+; on a 375px row it took the name's width, so there the
  // words alone in the tone, no pill. 21 Oct 2026 audit: the phone printed a
  // bare "KYC" for pending AND for rejected — told apart by colour only — so it
  // now says which, and the name (which truncates) gives up the room.
  if (status === 'pending')  return <><span className="lg:hidden whitespace-nowrap text-meta font-medium text-warn" title="KYC pending">KYC pending</span><Pill tone="warn" className="hidden lg:inline-flex">KYC pending</Pill></>;
  if (status === 'rejected') return <><span className="lg:hidden whitespace-nowrap text-meta font-medium text-neg" title="KYC rejected">KYC rejected</span><Pill tone="bad" className="hidden lg:inline-flex">KYC rejected</Pill></>;
  return null;
}

function PartyRow({ party, onToggle, dimmed = false, balances, kind = true, className = '' }: {
  party: PartySummary; dimmed?: boolean; balances?: Record<string, number>;
  /** Say the kind (Customer / Merchant / Desk account) — off when the list is
   *  already filtered to one kind. */
  kind?: boolean;
  className?: string;
} & ToggleProps) {
  const isGroup = isGroupParty(party);
  // Below lg there is no room for a column per asset, so every non-zero asset
  // renders as an inline signed chip. Nothing is truncated away — a party
  // holding four assets shows four chips and the row wraps.
  const chips = balances ? Object.entries(balances).filter(([, v]) => v !== 0) : [];
  const subtitle = partySubtitle(party, { compact: true, kind });
  return (
    <Link
      to={`/parties/${encodeURIComponent(party.slug)}?entity=dubaiotc`}
      className={`block py-2 max-lg:py-3 transition-colors ${dimmed ? 'opacity-70' : ''} ${className}`}
    >
      {/* 22 Oct 2026: the name WRAPS (it truncated at ~25 characters, and its kind word beside it to "Cu…"); the kind
          word stays beside the name when it fits and drops under it when it does not. KYC and the star keep the right. */}
      <div className="flex items-start gap-2 min-w-0">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
          {isGroup && <FolderOpen size={13} className="text-tg-hint shrink-0 self-center" />}
          <span className="who text-body min-w-0 break-words" title={partyName(party)}>{partyName(party)}</span>
          {needsNameReview(party.display_name) && <span className="text-tg-hint/70 text-meta shrink-0" title="Name needs a look">⚑</span>}
          {subtitle && <span className="text-meta text-tg-hint whitespace-nowrap">{subtitle}</span>}
        </div>
        <span className="flex items-center gap-1 shrink-0 self-center">
          {party.party_type !== 'own_account' && <KycMark status={party.kyc_status} />}
          <StarButton party={party} onToggle={onToggle} />
          {isGroup && <ChevronRight size={14} className="text-tg-hint" />}
        </span>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 pl-0.5">
          {chips.map(([a, v]) => (
            <span key={a} className="text-meta amt">
              <span className="text-tg-hint">{a}</span>{' '}
              <span className={v < 0 ? 'text-neg' : ''}>{fmtAmount(v, a)}</span>
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

function FavoriteCard({ party, balances, onToggle }: { party: PartySummary; balances?: Record<string, number> } & ToggleProps) {
  // Top 3 balances by magnitude; keys are already display labels post-collapse.
  const shown = balances
    ? Object.entries(collapseTotals(balances)).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])).slice(0, 3)
    : [];
  return (
    <Link
      to={`/parties/${encodeURIComponent(party.slug)}?entity=dubaiotc`}
      className="relative block bg-panel border border-stroke rounded-xl p-3 transition-colors hover:bg-panel-2 hover:border-tg-accent"
    >
      <StarButton party={party} onToggle={onToggle} className="absolute top-2 right-2" />
      {/* 22 Oct 2026: the name wraps here too — Name (one line, truncate, by its own law) cut "Alder Finch & Brightwater Ge…" and
          "Northern Gulf Commodities …" on a card 320 px wide. */}
      <div className="pr-7">
        <div className="flex min-h-[32px] min-w-0 flex-col justify-center">
          <span className="flex min-w-0 items-baseline gap-1">
            <span className="who text-body min-w-0 break-words" title={partyName(party)}>{partyName(party)}</span>
            {needsNameReview(party.display_name) && <span className="shrink-0 select-none text-meta text-tg-hint/70" title="Name needs a look">⚑</span>}
          </span>
          {partySubtitle(party) && <span className="text-meta text-tg-hint">{partySubtitle(party)}</span>}
        </div>
      </div>
      {/* No balances (the switch is off) = no strip under the name (21 Oct 2026
          audit); an empty bag shows no strip either — it is never "Square" here,
          the list cannot tell square from a ledger read that failed. */}
      {balances && shown.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {shown.map(([asset, v]) => (
            <div key={asset} className="flex justify-between items-baseline amt text-meta">
              <span className="text-tg-hint text-meta">{asset}</span>
              <span className={v < 0 ? 'text-neg' : v === 0 ? 'text-tg-hint' : ''}>{fmtAmount(v, asset)}</span>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
}

/** ONE LINE PER PARTY at lg+ too (20 Sept 2026, "the same clean-up on the
 *  desktop"): the name, the subtitle muted after it — the kind word off when
 *  the list is filtered to one kind — where every row was two lines, the
 *  second saying "Customer" under a list of customers. */
function PartyTableRow({ party, onToggle, dimmed = false, assetCols = [], balances, showKyc = true, kind = true }: {
  party: PartySummary; dimmed?: boolean;
  assetCols?: string[]; balances?: Record<string, number>; showKyc?: boolean;
  /** Say the kind — off when the list is already filtered to one kind. */
  kind?: boolean;
} & ToggleProps) {
  const nav = useNavigate();
  const isGroup = isGroupParty(party);
  const subtitle = partySubtitle(party, { compact: true, kind });
  return (
    <tr
      onClick={() => nav(`/parties/${encodeURIComponent(party.slug)}?entity=dubaiotc`)}
      className={`cursor-pointer transition-colors hover:bg-tg-secondary ${dimmed ? 'opacity-70' : ''}`}
    >
      <td className="px-3 py-2"><StarButton party={party} onToggle={onToggle} /></td>
      <td className="px-3 py-2">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          {isGroup && <FolderOpen size={13} className="text-tg-hint shrink-0 self-center" />}
          <span className="who text-body min-w-0 break-words" title={partyName(party)}>{partyName(party)}</span>
          {needsNameReview(party.display_name) && <span className="text-tg-hint/70 text-meta shrink-0" title="Name needs a look">⚑</span>}
          {subtitle && <span className="text-meta text-tg-hint whitespace-nowrap">{subtitle}</span>}
        </div>
      </td>
      {/* A bag with nothing in it is ONE dash across the columns — it was one
          under every asset (five of them on a row that owes nothing; 21 Oct 2026
          audit). It is a dash, not "Square": the list's bag is best-effort and a
          failed ledger read arrives as `{}` (Astra, 21 Oct). A row with no bag at
          all keeps its dashes (not read). */}
      {assetCols.length > 0 && balances && Object.keys(balances).length === 0 ? (
        <td colSpan={assetCols.length} className="px-3 py-2 text-center whitespace-nowrap text-body text-tg-hint" title="No balance listed">—</td>
      ) : assetCols.map((a) => (
        <td key={a} className="px-3 py-2 text-right whitespace-nowrap text-body">
          <BalanceCell v={balances?.[a]} asset={a} />
        </td>
      ))}
      {showKyc && <td className="px-3 py-2">{hasKycMark(party) && <KycMark status={party.kyc_status} />}</td>}
      <td className="px-3 py-2 text-tg-hint"><ChevronRight size={14} /></td>
    </tr>
  );
}
